import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import insightsApi from '../../api/insightsApi';
import ModernToggle from '../ModernToggle';
import SignalsTable from './SignalsTable';

// ── The two scales, and the one band table behind them ────────────────────────
// The prediction API returns ONE number per pillar. This screen shows it two
// ways, and the wording follows the direction of each:
//
//   SIGNAL score — the raw value, in [-1, 1], where HIGHER is BETTER. Shown as
//     a percentage on the pillar bars (+40% = healthy, -40% = a concern).
//     "Risk" is the wrong word here: a high number is a good outcome.
//   RISK score — the same value mirrored into [0, 1], where HIGHER is WORSE.
//     This is what the radar plots (0 = centre, 1 = outer ring).
//     risk = (1 - signal) / 2, i.e. normalise [-1, 1] → [0, 1] then invert.
//
// Because they are one measurement read in opposite directions, both scales
// share a single band table: thresholds are declared in SIGNAL space, the risk
// side is derived by converting back, and the colours are identical. Only the
// labels differ — a signal reads Very negative … Very positive, the same band
// as risk reads Critical … Minimal.
export const signalToRisk = (signal) => (1 - (Number(signal) || 0)) / 2;
// Rounded to 6 dp: without it, 1 - 2 * 0.475 lands on 0.050000000000000044 and a
// risk score sitting exactly on a band edge falls into the neighbouring band.
const riskToSignal = (risk) => Math.round((1 - 2 * (Number(risk) || 0)) * 1e6) / 1e6;

const toRiskScores = (scores) =>
    Object.fromEntries(
        Object.entries(scores || {}).map(([pillar, signal]) => [pillar, signalToRisk(signal)])
    );

// Ordered worst → best. `maxSignal` is an exclusive upper bound on the signal
// score; the risk bounds shown alongside are just signalToRisk() of those.
// Colours are chosen to stay legible as chip backgrounds under white text.
const SCORE_BANDS = [
    {
        key: 'very_negative',
        maxSignal: -0.25,
        color: '#b71c1c', rgb: '183, 28, 28',
        signalLabelId: 'risks.band_signal_very_negative', signalLabel: 'Very negative', signalRange: '-100% to -25%',
        riskLabelId: 'risks.band_risk_critical', riskLabel: 'Critical', riskRange: '0.625 to 1.000',
    },
    {
        key: 'negative',
        maxSignal: -0.05,
        color: '#e53935', rgb: '229, 57, 53',
        signalLabelId: 'risks.band_signal_negative', signalLabel: 'Negative', signalRange: '-25% to -5%',
        riskLabelId: 'risks.band_risk_high', riskLabel: 'High', riskRange: '0.525 to 0.625',
    },
    {
        key: 'neutral',
        maxSignal: 0.05,
        color: '#ed6c02', rgb: '237, 108, 2',
        signalLabelId: 'risks.band_signal_neutral', signalLabel: 'Neutral', signalRange: '-5% to +5%',
        riskLabelId: 'risks.band_risk_moderate', riskLabel: 'Moderate', riskRange: '0.475 to 0.525',
    },
    {
        key: 'positive',
        maxSignal: 0.25,
        color: '#43a047', rgb: '67, 160, 71',
        signalLabelId: 'risks.band_signal_positive', signalLabel: 'Positive', signalRange: '+5% to +25%',
        riskLabelId: 'risks.band_risk_low', riskLabel: 'Low', riskRange: '0.375 to 0.475',
    },
    {
        key: 'very_positive',
        maxSignal: Infinity,
        color: '#1b5e20', rgb: '27, 94, 32',
        signalLabelId: 'risks.band_signal_very_positive', signalLabel: 'Very positive', signalRange: '+25% to +100%',
        riskLabelId: 'risks.band_risk_minimal', riskLabel: 'Minimal', riskRange: '0.000 to 0.375',
    },
];

// Project a band onto one scale: same key/colours, scale-appropriate wording.
const bandOnScale = (band, scale) => ({
    key: band.key,
    color: band.color,
    bg: `rgba(${band.rgb}, 0.10)`,
    border: `rgba(${band.rgb}, 0.35)`,
    labelId: scale === 'risk' ? band.riskLabelId : band.signalLabelId,
    // English fallback, also used by consumers that render the label directly
    label: scale === 'risk' ? band.riskLabel : band.signalLabel,
    range: scale === 'risk' ? band.riskRange : band.signalRange,
});

const bandForSignal = (signal) =>
    SCORE_BANDS.find((b) => (Number(signal) || 0) < b.maxSignal) || SCORE_BANDS[SCORE_BANDS.length - 1];

// Band a SIGNAL score in [-1, 1] (higher = better) — the pillar bars.
export function signalBand(score) {
    return bandOnScale(bandForSignal(score), 'signal');
}

// Band a RISK score in [0, 1] (higher = worse) — the radar and the score cards.
// Converts back to signal space so both scales cut at exactly the same points.
export function riskBand(score) {
    return bandOnScale(bandForSignal(riskToSignal(score)), 'risk');
}

// ── Single-series radar chart (with optional snapshot overlay) ────────────────
function RiskRadarChart({ pillarScores, savedPillarScores }) {
    const [hoveredSeries, setHoveredSeries] = useState(null); // 'current' | 'saved' | null
    const pillars = Object.keys(pillarScores);
    const n = pillars.length;
    if (n === 0) return null;

    const SIZE = 400;
    const cx = SIZE / 2;
    const cy = SIZE / 2;
    const MAX_R = 140;
    const RING_FRACS = [0.25, 0.5, 0.75, 1];

    const toPoint = (fraction, index) => {
        const angle = -Math.PI / 2 + (index * 2 * Math.PI) / n;
        const r = fraction * MAX_R;
        return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
    };

    const buildPath = (scores) => {
        const pts = pillars.map((p, i) =>
            toPoint(Math.max(0, Math.min(1, scores[p] ?? 0)), i)
        );
        return {
            d: pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z',
            pts,
        };
    };

    const { d: pathD, pts: polyPoints } = buildPath(pillarScores);
    const saved = savedPillarScores ? buildPath(savedPillarScores) : null;

    const tooltipFor = (scores, label) =>
        [label, ...pillars.map((p) => `${p}: ${(scores[p] ?? 0).toFixed(3)}`)].join('\n');

    const hasSaved = !!saved;
    const currentDimmed = hoveredSeries === 'saved';
    const savedDimmed = hoveredSeries === 'current';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <svg
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                width={SIZE}
                height={SIZE}
                style={{ display: 'block', fontFamily: 'inherit', maxWidth: '100%', height: 'auto' }}
            >
                <defs>
                    <linearGradient id="riskFill" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#1565c0" stopOpacity={0.45} />
                        <stop offset="100%" stopColor="#1565c0" stopOpacity={0.1} />
                    </linearGradient>
                </defs>

                {/* Ring grid */}
                {RING_FRACS.map((frac) => (
                    <polygon
                        key={frac}
                        points={Array.from({ length: n }, (_, i) => {
                            const p = toPoint(frac, i);
                            return `${p.x},${p.y}`;
                        }).join(' ')}
                        fill="none"
                        stroke="rgba(21, 101, 192, 0.14)"
                        strokeWidth={1}
                    />
                ))}

                {/* Spokes */}
                {Array.from({ length: n }, (_, i) => {
                    const outer = toPoint(1, i);
                    return (
                        <line
                            key={`spoke-${i}`}
                            x1={cx} y1={cy}
                            x2={outer.x} y2={outer.y}
                            stroke="rgba(21, 101, 192, 0.18)"
                            strokeWidth={1}
                        />
                    );
                })}

                {/* Snapshot polygon — rendered below current so current is always on top */}
                {hasSaved && (
                    <g
                        onMouseEnter={() => setHoveredSeries('saved')}
                        onMouseLeave={() => setHoveredSeries(null)}
                        style={{ cursor: 'default' }}
                    >
                        <path
                            d={saved.d}
                            fill="rgba(150,150,150,0.15)"
                            stroke={savedDimmed ? 'rgba(150,150,150,0.25)' : 'rgba(100,100,100,0.55)'}
                            strokeWidth={hoveredSeries === 'saved' ? 2.5 : 1.5}
                            strokeDasharray="5 3"
                            strokeLinejoin="round"
                            style={{ transition: 'stroke 0.15s ease, stroke-width 0.15s ease, fill 0.15s ease' }}
                        >
                            <title>{tooltipFor(savedPillarScores, 'Snapshot')}</title>
                        </path>
                        {saved.pts.map((p, i) => (
                            <circle key={i} cx={p.x} cy={p.y} r={3.5} fill="rgba(100,100,100,0.5)" />
                        ))}
                    </g>
                )}

                {/* Current polygon */}
                <g
                    onMouseEnter={() => setHoveredSeries('current')}
                    onMouseLeave={() => setHoveredSeries(null)}
                    style={{ cursor: 'default' }}
                >
                    <path
                        d={pathD}
                        fill="url(#riskFill)"
                        stroke="#1565c0"
                        strokeWidth={hoveredSeries === 'current' ? 3 : 2}
                        strokeLinejoin="round"
                        fillOpacity={currentDimmed ? 0.18 : hoveredSeries === 'current' ? 0.6 : 0.42}
                        strokeOpacity={currentDimmed ? 0.4 : 1}
                        style={{ transition: 'fill-opacity 0.15s ease, stroke-opacity 0.15s ease, stroke-width 0.15s ease' }}
                    >
                        <title>{tooltipFor(pillarScores, 'Current')}</title>
                    </path>
                    {polyPoints.map((p, i) => {
                        const band = riskBand(pillarScores[pillars[i]]);
                        return (
                            <circle key={i} cx={p.x} cy={p.y} r={4.5} fill={band.color}
                                stroke="#fff" strokeWidth={1}
                                opacity={currentDimmed ? 0.3 : 0.95}
                                style={{ transition: 'opacity 0.15s ease' }}
                            >
                                <title>{`${pillars[i]}: ${(pillarScores[pillars[i]] ?? 0).toFixed(3)} — ${band.label}`}</title>
                            </circle>
                        );
                    })}
                </g>

                {/* Axis labels */}
                {pillars.map((pillar, i) => {
                    const lx = cx + (MAX_R + 34) * Math.cos(-Math.PI / 2 + (i * 2 * Math.PI) / n);
                    const ly = cy + (MAX_R + 34) * Math.sin(-Math.PI / 2 + (i * 2 * Math.PI) / n);
                    return (
                        <text
                            key={pillar}
                            x={lx}
                            y={ly}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={11}
                            fontWeight={600}
                            fill="#37474f"
                        >
                            {pillar}
                        </text>
                    );
                })}

                {/* Ring value labels on the top spoke */}
                {RING_FRACS.map((frac) => {
                    const p = toPoint(frac, 0);
                    return (
                        <text key={frac} x={p.x + 4} y={p.y} fontSize={9} fill="#9e9e9e">
                            {frac.toFixed(2)}
                        </text>
                    );
                })}
            </svg>

            {/* Risk-band legend — same bands and colours as the pillar bars
                above (they colour this chart's vertex dots), worded for the
                0–1 risk scale this chart plots */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%', mt: 0.5, justifyContent: 'center' }}>
                <ScoreScaleLegend scale="risk" centered />
                <ScoreScaleInfo scale="risk" />
            </Stack>

            {/* Snapshot legend — only shown when snapshot is active */}
            {hasSaved && (
                <Stack direction="row" spacing={3} sx={{ mt: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 22, height: 2, borderTop: '2px dashed rgba(100,100,100,0.55)', mt: '1px' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            <FormattedMessage id="risks.snapshot_label" defaultMessage="Snapshot" />
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 22, height: 2, bgcolor: '#1565c0', borderRadius: 1 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            <FormattedMessage id="risks.current_label" defaultMessage="Current" />
                        </Typography>
                    </Stack>
                </Stack>
            )}
        </Box>
    );
}

// ── Scale legend ──────────────────────────────────────────────────────────────
// One component, two wordings. `scale="signal"` documents the pillar bars
// (-100%…+100%, higher is better); `scale="risk"` documents the radar (0…1,
// higher is worse) and lists the bands least-risky first so the order tracks
// the radar's centre-to-edge direction. Same colours either way.
export function ScoreScaleLegend({ scale, withDescription = false, centered = false }) {
    const bands = scale === 'risk' ? [...SCORE_BANDS].reverse() : SCORE_BANDS;
    const helpId = scale === 'risk' ? 'risks.risk_help' : 'risks.signal_help';
    const helpDefault =
        scale === 'risk'
            ? 'The pillar signal scores mirrored into a risk score from 0 to 1, which is what this chart plots: higher means more risk, so 0 (centre) is the healthiest reading and 1 (outer ring) the most concerning.'
            : 'Signal scores predicted by our model for each pillar, from -100% to +100%. Positive is good, negative is a concern, and values close to zero are neutral.';

    return (
        <Box>
            {withDescription && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1, lineHeight: 1.5 }}>
                    <FormattedMessage id={helpId} defaultMessage={helpDefault} />
                </Typography>
            )}
            <Stack
                direction="row"
                sx={{ flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5, alignItems: 'center', justifyContent: centered ? 'center' : 'flex-start' }}
            >
                {bands.map((band) => {
                    const view = bandOnScale(band, scale);
                    return (
                        <Stack key={view.key} direction="row" alignItems="center" spacing={0.5}>
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '2px',
                                    bgcolor: view.color,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: withDescription ? 'inherit' : 'text.secondary', whiteSpace: 'nowrap' }}
                            >
                                <FormattedMessage id={view.labelId} defaultMessage={view.label} />
                                {` (${view.range})`}
                            </Typography>
                        </Stack>
                    );
                })}
            </Stack>
        </Box>
    );
}

// The ⓘ affordance on its own: hovering it explains the scale and lists the
// bands. Charts that don't colour anything by band (the compare radar draws one
// colour per wave) use this alone, so no swatch row implies a colour coding the
// chart doesn't have.
export function ScoreScaleInfo({ scale, placement = 'top' }) {
    const intl = useIntl();
    return (
        <Tooltip
            title={<ScoreScaleLegend scale={scale} withDescription />}
            placement={placement}
            componentsProps={{ tooltip: { sx: { maxWidth: 340, p: 1.5 } } }}
        >
            <IconButton
                size="small"
                aria-label={intl.formatMessage(
                    scale === 'risk'
                        ? { id: 'risks.risk_help_aria', defaultMessage: 'About these risk scores' }
                        : { id: 'risks.signal_help_aria', defaultMessage: 'About these signal scores' }
                )}
                sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: '#1565c0' } }}
            >
                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
        </Tooltip>
    );
}

// Fixed pillar bars built from the UNFILTERED prediction. Clicking a bar selects
// that pillar for the signal table; the values themselves never change on filter.
function SignalPillarBars({ scores, selected, onSelect, action }) {
    const intl = useIntl();
    const entries = Object.entries(scores || {});
    if (entries.length === 0) return null;
    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0d47a1' }}>
                        <FormattedMessage id="risks.pillar_scores" defaultMessage="Pillar signal scores" />
                    </Typography>
                    <ScoreScaleInfo scale="signal" placement="right" />
                </Stack>
                {action}
            </Box>
            <Box sx={{ mb: 1.5 }}>
                <ScoreScaleLegend scale="signal" />
            </Box>
            <Stack spacing={1.25}>
                {entries.map(([pillar, score]) => {
                    const isSel = selected === pillar;
                    const band = signalBand(score);
                    const color = band.color;
                    return (
                        <Box
                            key={pillar}
                            onClick={() => onSelect(pillar)}
                            sx={{
                                cursor: 'pointer',
                                opacity: isSel ? 1 : 0.75,
                                transition: 'opacity 0.2s ease, transform 0.2s ease',
                                '&:hover': { opacity: 1, transform: 'translateX(3px)' },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: isSel ? 700 : 500, color: isSel ? '#0d47a1' : 'text.primary' }}
                                >
                                    {pillar}
                                </Typography>
                                {/* band label in the tooltip so colour is never the only cue */}
                                <Tooltip
                                    title={`${intl.formatMessage({ id: band.labelId, defaultMessage: band.label })} (${band.range})`}
                                    placement="left"
                                >
                                    <Chip
                                        label={`${(Number(score) || 0) > 0 ? '+' : ''}${((Number(score) || 0) * 100).toFixed(0)}%`}
                                        size="small"
                                        sx={{ bgcolor: color, color: '#fff', fontWeight: 700 }}
                                    />
                                </Tooltip>
                            </Box>
                            <Box sx={{ position: 'relative', height: 16, bgcolor: '#eceff1', borderRadius: 1, overflow: 'hidden', border: isSel ? '2px solid #1565c0' : '2px solid transparent' }}>
                                <Box sx={{ position: 'absolute', left: '50%', width: `${Math.abs(Number(score) || 0) * 50}%`, height: '100%', bgcolor: color, transform: (Number(score) || 0) < 0 ? 'translateX(-100%)' : 'none', transition: 'width 0.3s ease' }} />
                                <Box sx={{ position: 'absolute', left: '50%', width: 2, height: '100%', bgcolor: '#90a4ae', transform: 'translateX(-50%)' }} />
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RisksSection({ reportId, riskFilters, signalResults, language }) {
    const intl = useIntl();
    const [selectedFilters, setSelectedFilters] = useState({});
    const [pillarScores, setPillarScores] = useState(null);
    const [savedPillarScores, setSavedPillarScores] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Raw signal scores (-1..1) from the initial UNFILTERED predict — feeds the
    // fixed pillar bars and never changes when filters change.
    const [baseSignalScores, setBaseSignalScores] = useState(null);
    // Pillar selected in the bars (drives which questions the signal table shows).
    const [selectedPillar, setSelectedPillar] = useState(null);
    // Which lower view is shown: the interactive radar or the signal table.
    const [viewMode, setViewMode] = useState('radar');
    // AI risk-insight state
    const [insightOpen, setInsightOpen] = useState(false);
    const [insightLoading, setInsightLoading] = useState(false);
    const [insightError, setInsightError] = useState(null);
    const [insightData, setInsightData] = useState(null);
    const isInitialMount = useRef(true);
    const debounceRef = useRef(null);
    const streamAbortRef = useRef(null);

    const filterKeys = riskFilters ? Object.keys(riskFilters) : [];

    // Build the active-filter object the same way fetchRiskScores does:
    // only keys with a non-empty selection are included.
    const buildActiveFilters = (filters) => {
        const activeFilters = {};
        Object.entries(filters).forEach(([key, vals]) => {
            if (vals && vals.length > 0) activeFilters[key] = vals;
        });
        return activeFilters;
    };

    const fetchRiskScores = useCallback(async (filters, opts = {}) => {
        try {
            setLoading(true);
            setError(null);
            const result = await insightsApi.getRiskPrediction([reportId], buildActiveFilters(filters));
            const reportKey = String(reportId);
            const raw = result[reportKey] ?? result;
            setPillarScores(toRiskScores(raw));
            // Capture the unfiltered baseline once for the fixed pillar bars.
            if (opts.isBase) setBaseSignalScores(raw);
        } catch {
            setError(intl.formatMessage({
                id: 'risks.fetch_error',
                defaultMessage: 'Failed to load risk predictions.',
            }));
        } finally {
            setLoading(false);
        }
    }, [reportId, intl]);

    // Open the modal and stream a fresh explanation for the CURRENT radar slice
    // every time. Meta (scores/drivers/slice/n_quotes) lands first so the header
    // chips render immediately; then tokens append to the explanation live, so
    // the markdown grows in place (typewriter). Because we regenerate on each
    // open, the modal always reflects the live filters.
    const handleOpenInsight = () => {
        // Tear down any in-flight stream from a previous open.
        if (streamAbortRef.current) {
            streamAbortRef.current();
            streamAbortRef.current = null;
        }

        setInsightOpen(true);
        setInsightLoading(true);
        setInsightError(null);
        setInsightData(null);

        streamAbortRef.current = insightsApi.streamRiskInsight(
            reportId,
            buildActiveFilters(selectedFilters),
            {
                onMeta: (meta) => {
                    setInsightData((prev) => ({
                        ...(prev || {}),
                        ...meta,
                        // Convert signal scores [-1, 1] → risk scores [0, 1] so the
                        // modal's per-pillar cards match the radar chart.
                        ...(meta.scores ? { scores: toRiskScores(meta.scores) } : {}),
                        explanation: prev?.explanation || '',
                        streaming: true,
                    }));
                },
                onToken: (t) => {
                    setInsightData((prev) => ({
                        ...(prev || {}),
                        explanation: (prev?.explanation || '') + t,
                        streaming: true,
                    }));
                },
                onDone: () => {
                    setInsightLoading(false);
                    setInsightData((prev) => (prev ? { ...prev, streaming: false } : prev));
                    streamAbortRef.current = null;
                },
                onError: () => {
                    setInsightLoading(false);
                    setInsightError(intl.formatMessage({
                        id: 'risks.ai_insight_error',
                        defaultMessage: 'Failed to generate AI insight. Please try again.',
                    }));
                    streamAbortRef.current = null;
                },
            }
        );
    };

    const handleCloseInsight = () => {
        if (streamAbortRef.current) {
            streamAbortRef.current();
            streamAbortRef.current = null;
        }
        setInsightOpen(false);
        setInsightLoading(false);
        setInsightData(null);
        setInsightError(null);
    };

    // Abort any in-flight stream if the component unmounts mid-generation.
    useEffect(() => () => {
        if (streamAbortRef.current) streamAbortRef.current();
    }, []);

    // Default the selected pillar to the first once the baseline scores arrive.
    useEffect(() => {
        if (baseSignalScores && Object.keys(baseSignalScores).length > 0 && !selectedPillar) {
            setSelectedPillar(Object.keys(baseSignalScores)[0]);
        }
    }, [baseSignalScores, selectedPillar]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            fetchRiskScores({}, { isBase: true });
            return;
        }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchRiskScores(selectedFilters), 2000);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters]);

    const handleToggle = (key) => (_, next) => {
        setSelectedFilters((prev) => ({ ...prev, [key]: next ?? [] }));
    };

    const handleSnapshot = () => {
        if (pillarScores && Object.keys(pillarScores).length > 0) {
            setSavedPillarScores({ ...pillarScores });
        }
    };

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, #f8faff 0%, #eef3fb 100%)',
                borderRadius: 3,
                border: '1px solid rgba(21, 101, 192, 0.12)',
                p: { xs: 2, sm: 3 },
                boxShadow: '0 8px 18px rgba(13, 71, 161, 0.06)',
            }}
        >
            {/* Fixed pillar bars from the unfiltered prediction (shown in both views).
                The Prediction/Signals switch sits inline with the bars' title. */}
            <SignalPillarBars
                scores={baseSignalScores}
                selected={selectedPillar}
                onSelect={setSelectedPillar}
                action={
                    <ModernToggle
                        leftLabel={intl.formatMessage({ id: 'risks.view_radar', defaultMessage: 'Prediction' })}
                        rightLabel={intl.formatMessage({ id: 'risks.view_signals', defaultMessage: 'Signals' })}
                        checked={viewMode === 'signals'}
                        onChange={(checked) => setViewMode(checked ? 'signals' : 'radar')}
                    />
                }
            />

            {viewMode === 'signals' ? (
                <SignalsTable
                    signalResults={signalResults}
                    reportId={reportId}
                    selectedPillar={selectedPillar}
                    language={language}
                />
            ) : (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
                {/* Filter panel — only when risk_filters data is available */}
                {filterKeys.length > 0 && (
                    <Box
                        sx={{
                            minWidth: { xs: '100%', md: 280 },
                            p: 2,
                            borderRadius: 2.5,
                            background: 'rgba(255,255,255,0.7)',
                            border: '1px solid rgba(21, 101, 192, 0.12)',
                        }}
                    >
                        <Stack spacing={2.5}>
                            {filterKeys.map((key) => (
                                <FormControl key={key} component="fieldset" variant="standard" sx={{ m: 0 }}>
                                    <FormLabel
                                        component="legend"
                                        sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 1, textTransform: 'capitalize' }}
                                    >
                                        {intl.formatMessage({
                                            id: `risks.filter_${key}`,
                                            defaultMessage: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                                        })}
                                    </FormLabel>
                                    <ToggleButtonGroup
                                        value={selectedFilters[key] || []}
                                        onChange={handleToggle(key)}
                                        exclusive={false}
                                        sx={{
                                            flexWrap: 'wrap',
                                            gap: 0.75,
                                            '& .MuiToggleButton-root': {
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                px: 1.5,
                                                borderRadius: 1.75,
                                                borderColor: 'rgba(21, 101, 192, 0.2)',
                                                color: 'text.secondary',
                                                fontSize: 12,
                                            },
                                            '& .MuiToggleButton-root.Mui-selected': {
                                                bgcolor: 'rgba(21, 101, 192, 0.12)',
                                                color: '#0d47a1',
                                                borderColor: 'rgba(21, 101, 192, 0.45)',
                                            },
                                        }}
                                    >
                                        {(riskFilters[key] || []).map((option) => (
                                            <ToggleButton key={option} value={option} size="small">
                                                {option}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </FormControl>
                            ))}
                            <Typography variant="caption" color="text.secondary">
                                <FormattedMessage
                                    id="risks.filter_hint"
                                    defaultMessage="Leave all unselected to include everyone. Changes apply after 2 seconds."
                                />
                            </Typography>
                        </Stack>
                    </Box>
                )}

                {/* Chart panel */}
                <Box sx={{ flex: 1, minWidth: 600, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Header actions — only visible when there is chart data */}
                    {!loading && !error && pillarScores && Object.keys(pillarScores).length > 0 && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ alignSelf: 'flex-end', mb: -1 }}
                        >
                            <Tooltip
                                title={intl.formatMessage({
                                    id: 'risks.ai_insight_tooltip',
                                    defaultMessage: 'Explain these risk scores with AI',
                                })}
                                placement="left"
                            >
                                {/* span keeps the tooltip working while the button is disabled */}
                                <span>
                                    <IconButton
                                        onClick={handleOpenInsight}
                                        disabled={insightLoading}
                                        aria-label={intl.formatMessage({
                                            id: 'risks.ai_insight_tooltip',
                                            defaultMessage: 'Explain these risk scores with AI',
                                        })}
                                        sx={{
                                            p: 1.25,
                                            color: '#1565c0',
                                            transition:
                                                'color 0.15s ease, background-color 0.15s ease, transform 0.15s ease',
                                            '&:hover': {
                                                color: '#0d47a1',
                                                bgcolor: 'rgba(21, 101, 192, 0.12)',
                                                transform: 'scale(1.08)',
                                            },
                                        }}
                                    >
                                        {insightLoading ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            <TipsAndUpdatesOutlinedIcon sx={{ fontSize: 28 }} />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={intl.formatMessage({
                                    id: savedPillarScores ? 'risks.snapshot_replace' : 'risks.snapshot_save',
                                    defaultMessage: savedPillarScores
                                        ? 'Replace snapshot with current polygon'
                                        : 'Pin current polygon as snapshot for comparison',
                                })}
                                placement="left"
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleSnapshot}
                                    sx={{
                                        color: savedPillarScores ? '#1565c0' : 'text.disabled',
                                        transition: 'color 0.2s ease',
                                        '&:hover': { color: '#1565c0' },
                                    }}
                                >
                                    {savedPillarScores ? (
                                        <PushPinIcon fontSize="small" />
                                    ) : (
                                        <PushPinOutlinedIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}

                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
                            <CircularProgress size={32} />
                        </Box>
                    )}
                    {!loading && error && (
                        <Alert severity="error">{error}</Alert>
                    )}
                    {!loading && !error && pillarScores && Object.keys(pillarScores).length === 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 220,
                                gap: 1.5,
                                color: 'text.disabled',
                            }}
                        >
                            <AssessmentOutlinedIcon sx={{ fontSize: 56, opacity: 0.35 }} />
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                <FormattedMessage
                                    id="risks.no_data"
                                    defaultMessage="No risk data available for this report."
                                />
                            </Typography>
                        </Box>
                    )}
                    {!loading && !error && pillarScores && Object.keys(pillarScores).length > 0 && (
                        <RiskRadarChart
                            pillarScores={pillarScores}
                            savedPillarScores={savedPillarScores}
                        />
                    )}

                </Box>
            </Stack>
            )}

            {/* AI risk-insight modal — generated fresh for the current slice on each open */}
            <Dialog
                open={insightOpen}
                onClose={handleCloseInsight}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: { borderRadius: 3, border: '1px solid rgba(21, 101, 192, 0.12)' },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        pr: 6,
                        fontWeight: 700,
                        color: '#0d47a1',
                        background: 'linear-gradient(135deg, #f8faff 0%, #eef3fb 100%)',
                    }}
                >
                    <TipsAndUpdatesOutlinedIcon fontSize="small" sx={{ color: '#1565c0' }} />
                    <FormattedMessage id="risks.ai_insight_title" defaultMessage="AI Risk Insight" />
                    {(() => {
                        const active = buildActiveFilters(selectedFilters);
                        const vals = Object.values(active).flat();
                        const slice = vals.length
                            ? vals.join(', ')
                            : intl.formatMessage({ id: 'risks.ai_insight_all', defaultMessage: 'All respondents' });
                        return (
                            <Box component="span" sx={{ fontWeight: 600, color: '#1565c0' }}>
                                {`— ${slice}`}
                            </Box>
                        );
                    })()}
                    <IconButton
                        aria-label={intl.formatMessage({ id: 'risks.ai_insight_close', defaultMessage: 'Close' })}
                        onClick={handleCloseInsight}
                        size="small"
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {/* (a) the slice this explanation describes */}
                    <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                        {(() => {
                            const active = buildActiveFilters(selectedFilters);
                            const chips = Object.entries(active).flatMap(([key, vals]) =>
                                vals.map((v) => `${key}: ${v}`)
                            );
                            if (chips.length === 0) {
                                return (
                                    <Chip
                                        size="small"
                                        label={intl.formatMessage({
                                            id: 'risks.ai_insight_all',
                                            defaultMessage: 'All respondents',
                                        })}
                                        sx={{ bgcolor: 'rgba(21, 101, 192, 0.08)', color: '#0d47a1', fontWeight: 600 }}
                                    />
                                );
                            }
                            return chips.map((label) => (
                                <Chip
                                    key={label}
                                    size="small"
                                    label={label}
                                    sx={{
                                        bgcolor: 'rgba(21, 101, 192, 0.12)',
                                        color: '#0d47a1',
                                        fontWeight: 600,
                                        textTransform: 'capitalize',
                                    }}
                                />
                            ));
                        })()}
                    </Stack>

                    {/* Spinner only until the first stream frame lands */}
                    {insightLoading && !insightData && !insightError && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1.5,
                                py: 5,
                            }}
                        >
                            <CircularProgress size={28} />
                            <Typography variant="body2" color="text.secondary">
                                <FormattedMessage
                                    id="risks.ai_insight_loading"
                                    defaultMessage="Generating insight for the current slice…"
                                />
                            </Typography>
                        </Box>
                    )}

                    {insightError && (
                        <Alert severity="error">{insightError}</Alert>
                    )}

                    {!insightError && insightData && (
                        <>
                            {/* (b) per-pillar scores as compact, color-coded score cards.
                                 Value is tinted by band on a tinted card with a colored
                                 left accent; the band label lives in the title tooltip so
                                 colour is never the only cue. */}
                            {insightData.scores && Object.keys(insightData.scores).length > 0 && (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                        gap: '10px',
                                        mb: 2.5,
                                    }}
                                >
                                    {Object.entries(insightData.scores).map(([pillar, val]) => {
                                        const band = riskBand(val);
                                        const name = pillar.charAt(0).toUpperCase() + pillar.slice(1).toLowerCase();
                                        return (
                                            <Box
                                                key={pillar}
                                                title={`${name} — ${band.label}`}
                                                sx={{
                                                    bgcolor: band.bg,
                                                    borderLeft: `3px solid ${band.color}`,
                                                    borderRadius: '0 8px 8px 0',
                                                    px: 1.25,
                                                    py: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        color: 'text.secondary',
                                                        lineHeight: 1.2,
                                                        mb: 0.25,
                                                    }}
                                                >
                                                    {name}
                                                </Typography>
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: 16,
                                                        fontWeight: 500,
                                                        color: band.color,
                                                        lineHeight: 1.2,
                                                        fontVariantNumeric: 'tabular-nums',
                                                    }}
                                                >
                                                    {Number(val).toFixed(3)}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            {/* (c) the AI explanation (markdown, GFM tables/task-lists),
                                 streamed live; a blinking cursor trails while tokens arrive */}
                            {(insightData.explanation || insightData.streaming) && (
                                <Box
                                    sx={{
                                        fontSize: 15,
                                        lineHeight: 1.7,
                                        color: 'text.primary',
                                        overflowX: 'auto',
                                        '& p': { mt: 0, mb: 1.5 },
                                        '& p:last-child': { mb: 0 },
                                        '& ul, & ol': { mt: 0, mb: 1.5, pl: 3 },
                                        '& li': { mb: 0.5 },
                                        '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 13, my: 1 },
                                        '& th, & td': { border: '0.5px solid rgba(21,101,192,0.15)', padding: '6px 10px', textAlign: 'left' },
                                        '& th': { background: 'rgba(21,101,192,0.06)', color: '#0d47a1', fontWeight: 500 },
                                        '@keyframes risk-cursor-blink': { '50%': { opacity: 0 } },
                                    }}
                                >
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{insightData.explanation}</ReactMarkdown>
                                    {insightData.streaming && (
                                        <Box
                                            component="span"
                                            aria-hidden="true"
                                            sx={{
                                                display: 'inline-block',
                                                ml: '1px',
                                                color: '#1565c0',
                                                fontWeight: 700,
                                                animation: 'risk-cursor-blink 1s step-end infinite',
                                            }}
                                        >
                                            ▍
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* (d) grounded-response count (we now paraphrase, not quote) */}
                            {typeof insightData.n_quotes === 'number' && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 2 }}
                                >
                                    <FormattedMessage
                                        id="risks.ai_insight_quotes"
                                        defaultMessage="Grounded in {n, plural, one {# employee response} other {# employee responses}}."
                                        values={{ n: insightData.n_quotes }}
                                    />
                                </Typography>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button
                        onClick={handleCloseInsight}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#0d47a1' }}
                    >
                        <FormattedMessage id="risks.ai_insight_close" defaultMessage="Close" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
